import { race } from "racing-bars";

interface ChartOptions {
    autoRun: boolean;
    labelsPosition: 'outside' | 'inside' | 'none';
    labelsWidth: number;
    showIcons: boolean;
    mouseControls: boolean;
    tickDuration: number;
    theme: string;
    startDate?: string;
}

const chartOptions: ChartOptions = {
    autoRun: false,
    labelsPosition: 'outside' as 'outside' | 'inside' | 'none',
    labelsWidth: 62,
    showIcons: true,
    mouseControls: true,
    tickDuration: 150,
    theme: 'dark'
};

const calculateChartHeight = (numCoins) => {
    return (300 +(numCoins * 20)) + 'px';
}

class RaceBarChart {
    private rootElement: HTMLElement;
    private overlayElement: HTMLElement;
    private playButtonElement: HTMLElement;
    private chartElement: HTMLElement;
    private selectedCoins: Set<string>;
    private coinsData: Map<string, any[]>;
    private selectedCoinsRows: any[];
    private chart: any;
    private startDate: string | null = null;

    constructor(rootElement, selectedCoins, coinsData) {
        this.rootElement = rootElement;
        this.overlayElement = rootElement.querySelector('.chart-overlay');
        this.playButtonElement = rootElement.querySelector('.play-button');
        this.chartElement = rootElement.querySelector('.race');
        this.selectedCoins = selectedCoins;
        this.coinsData = coinsData;
        this.selectedCoinsRows = [];
        this.chart = null;

        this.initialize();
    }

    async initialize() {
        this.chart = await race(this.selectedCoinsRows, this.chartElement, chartOptions);
        this.playButtonElement.addEventListener('click', () => {
            this.hideOverlay();
            this.chart.play();
        });
    }

    showLoading() {
        this.overlayElement.classList.add('visible', 'loading');
    }

    showPlayButton() {
        this.overlayElement.classList.remove('loading');
        this.overlayElement.classList.add('visible');
    }

    hideOverlay() {
        this.overlayElement.classList.remove('visible', 'loading');
    }

    private updateSelectedCoinsRows() {
        this.selectedCoinsRows = Array.from(this.selectedCoins)
            .flatMap(coinId => {
                const coinData = this.coinsData.get(coinId) || [];
                if (this.startDate) {
                    const startDate = new Date(this.startDate);
                    return coinData.filter(dataPoint => {
                        const pointDate = new Date(dataPoint.date);
                        return pointDate >= startDate;
                    });
                }
                return coinData;
            });
    }

    setStartDate(date: string | null) {
        this.startDate = date;
        const options = { ...chartOptions };
        if (date) {
            options.startDate = date;
        }
        this.updateChart(true, options);
    }

    play() {
        this.chart.play();
    }

    pause() {
        this.chart.pause();
    }

    async updateChart(shouldShowPlay = true, options = chartOptions) {
        this.showLoading();
        
        // Ensure minimum loading time of 300ms
        await new Promise(resolve => setTimeout(resolve, 300));
        
        this.updateChartHeight(this.selectedCoins.size);
        this.updateSelectedCoinsRows();
        
        this.chartElement.innerHTML = '';
        this.chart = await race(this.selectedCoinsRows, this.chartElement, options);
        this.chart.pause();
        
        if (shouldShowPlay) {
            this.showPlayButton();
        } else {
            this.hideOverlay();
            this.chart.play();
        }
    }

    private updateChartHeight(numCoins) {
        this.chartElement.style.height = calculateChartHeight(numCoins);
    }
}

export default RaceBarChart;